import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { useParams} from 'react-router-dom';
import { selectPartiesInvolvedStatus } from '../../../../partiesinvolved/logic/PartiesInvolvedSelectors';
import ReportsSidebar, { PartiesInvolvedSkeletonLoader } from '../../../../partiesinvolved/components/PartiesInvolvedSidebar';
import { ReportSkeleton } from '../../../../../components/ui/loading/ReportSkeleton';
import ReportHeader from '../../CommonReportHeader';
import styles from './ROPage.module.css';
import ROContentContainer from './ROContentContainer';
import { createROTabItems } from './utils';
import { SaveButtonV2 } from '../../../../../components/ui/button/v2/SaveButton';
import { fetchLegacyReasonedOrdersThunk, fetchReasonedOrdersThunk,upsertLegacyReasonedOrderThunk, upsertReasonedOrderThunk } from '../logic/thunks';
import { selectROReportsData, selectROStatus } from '../logic/slice';
import { isReportsPolicyNormalizationEnabled } from '../../../../configs/logic/Slice';
import { getAllPartyPerpetrators } from '../../../../../features/stakeholder/logic/stakeholderSlice';


export const ReasonedOrderPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { id: case_id } = useParams<{ id: string }>();
  
  const [activePartyID, setActivePartyID] = useState<string | null>(null);
  const [localROContent, setLocalROContent] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  
  const roReports = useAppSelector(selectROReportsData);
  const roStatus = useAppSelector(selectROStatus);
  const isSaving = roStatus === 'saving';

  const parties = useAppSelector(getAllPartyPerpetrators);
  const partiesStatus = useAppSelector(selectPartiesInvolvedStatus);
  const isPolicyNormalizationEnabled = useAppSelector(isReportsPolicyNormalizationEnabled);

  const isPartiesLoading = partiesStatus === "loading"
  const isroLoading = roStatus === "loading"

  const isDataLoading = isroLoading || isPartiesLoading;

  useEffect(() => {
    if (!isPartiesLoading && parties.length > 0 && !activePartyID) {
        setActivePartyID(parties[0].party_id);
    }
  }, [isPartiesLoading, parties, activePartyID]);

  useEffect(() => {
    if (case_id) {
        dispatch(isPolicyNormalizationEnabled
        ? fetchReasonedOrdersThunk({ case_id })
        : fetchLegacyReasonedOrdersThunk({ case_id })
      );
    }
  }, [case_id, dispatch,isPolicyNormalizationEnabled]);

  const selectedRO = activePartyID ? roReports[activePartyID] : undefined;

  useEffect(() => {
    if (selectedRO?.content) {
        setLocalROContent(selectedRO.content);
    } else {
      setLocalROContent('');
    }
  }, [selectedRO,activePartyID,]);

  useEffect(() => {
    const currentContent = localROContent || '';
    const originalContent = selectedRO?.content || '';
    const hasChanges = originalContent !== currentContent;
    setHasUnsavedChanges(hasChanges);
  }, [localROContent, selectedRO?.content]);

  const handlePartySelect = (party_id: string) => {
    setActivePartyID(party_id);
  };

  const handleNoticeChange = (partyId: string, content: string) => {
    if (partyId === activePartyID) {
      setLocalROContent(content);
    }
  };

  const handleSave = () => {
    if (!case_id || !activePartyID || !hasUnsavedChanges) return;

    dispatch(isPolicyNormalizationEnabled
     ? upsertReasonedOrderThunk({
        case_id,
        report_id: selectedRO?.report_id ?? activePartyID,
        request: {
          content: localROContent,
          party_id: activePartyID,
        }
      })

     : upsertLegacyReasonedOrderThunk({
        case_id,
        report_id: selectedRO?.report_id ?? activePartyID,
        request: {
          party_id: activePartyID,
          content: localROContent
        }
      })
    );
  }

  const tabItems = createROTabItems(parties, roReports, isDataLoading);

  const selectedParty = parties.find(party => party.party_id === activePartyID);
  const selectedPartyName = selectedParty?.name;
  const shouldGenerateRO = selectedParty?.generate_ro ?? false;


  return (
  <>
    {/* Top header with Save & Navigation Breadcrumb - Outside the main container */}
    <div className={`${styles.topHeader} flex items-center justify-end`}>
      <SaveButtonV2
        disabled={!hasUnsavedChanges || isSaving}
        size="sm"
        onClick={handleSave}
        buttonText={isSaving ? 'Saving...' : 'Save'}
      />
    </div>

    {/* Main page container with sidebar and content */}
    <div className={styles.pageContainer}>
      {/* Parties Involved Sidebar */}
      <div className={styles.sidebarWrapper}>
        {isPartiesLoading ? (
            <PartiesInvolvedSkeletonLoader />
        ) : (
            <ReportsSidebar
              title='Reasoned Orders'
              items={tabItems}
              activeSelection={activePartyID}
              onSelectionChange={handlePartySelect}
            />
        )}
      </div>
      
      {/* Main Content Area */}
      <div className={styles.mainContent}>
        {isDataLoading ? (
          <ReportSkeleton />
        ) : 
        selectedParty && (
          <div className={styles.contentArea}>
            <ReportHeader
              partyName={selectedParty.name}
              partyType={selectedParty.party_type}
            />
            <div>
              {/* RO Container */}
              <ROContentContainer
                key={activePartyID}
                partyId={activePartyID ?? ''}
                partyName={selectedPartyName ?? ''}
                noticeData={selectedRO}
                currentContent={localROContent}
                onNoticeChange={handleNoticeChange}
                generateRO={shouldGenerateRO}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  </>
);
};
